import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import type { SlideProps } from '@/components/SlideWizard'
import { useSlideCopy } from '@/lib/i18n'
import { copy } from './Slide9bPractice.copy'

const ease = [0.16, 1, 0.3, 1] as const

export default function Slide9bPractice({ onComplete }: SlideProps) {
  const c = useSlideCopy(copy, {
    skipSpellingFor: ['fakeAmount', 'fakePhone', 'retailerName'],
  })
  const ROUTES = [
    { label: c.route1Label, safe: false, feedback: c.route1Feedback },
    { label: c.route2Label, safe: true, feedback: c.route2Feedback },
    { label: c.route3Label, safe: false, feedback: c.route3Feedback },
    { label: c.route4Label, safe: true, feedback: c.route4Feedback },
  ] as const

  const [selections, setSelections] = useState<Set<number>>(new Set())
  const [submitted, setSubmitted] = useState(false)

  const toggle = (i: number) => {
    if (submitted) return
    setSelections((prev) => {
      const next = new Set(prev)
      if (next.has(i)) next.delete(i)
      else next.add(i)
      return next
    })
  }

  const handleSubmit = () => {
    setSubmitted(true)
  }

  useEffect(() => {
    if (!submitted) return
    onComplete()
  }, [submitted, onComplete])

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 mx-auto max-w-5xl">
      <div className="grid w-full grid-cols-1 items-start gap-10 lg:grid-cols-[minmax(0,0.7fr)_minmax(0,1.3fr)] lg:items-center lg:gap-9">
        {/* SMS mockup */}
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.08, ease }}
          className="mx-auto w-full max-w-sm"
        >
          <div className="relative">
            <div className="absolute -start-[3px] top-24 h-8 w-[2px] rounded-full bg-slate-600/45" />
            <div className="overflow-hidden rounded-t-[2.5rem] border-[4px] border-b-0 border-slate-900">
              <div className="bg-slate-50">
                <div className="relative flex items-center justify-between px-6 pt-3">
                  <span className="text-[13px] font-semibold text-slate-900">9:41</span>
                  <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 h-[22px] w-[90px] rounded-full bg-slate-800" />
                  <StatusBarIcons />
                </div>
                <div className="px-4 pb-3 pt-2 text-center">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-slate-500">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 20a6 6 0 00-12 0" />
                      <circle cx="12" cy="10" r="4" />
                    </svg>
                  </div>
                  <p className="mt-2 text-[15px] font-semibold text-slate-900">
                    {c.contactName}
                  </p>
                </div>
              </div>

              <div className="border-t border-slate-200/80 bg-white px-4 pb-5 pt-4 text-start">
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25, delay: 0.5, ease }}
                  className="mb-2 text-center text-[12px] font-medium text-slate-400"
                >
                  {c.todayPrefix}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 22, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.42, delay: 0.62, ease }}
                  className="rounded-2xl rounded-es-none bg-slate-200/70 px-4 py-3 origin-top-left rtl:origin-top-right"
                >
                  <p className="text-[15px] leading-relaxed text-slate-800">
                    {c.smsBefore}{c.fakeAmount}{c.smsMid}{c.retailerName}{c.smsAfter}{c.fakePhone}{c.smsTrailer}
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Routes to pick */}
        <div className="mx-auto w-full max-w-xl lg:max-w-none lg:border-s lg:border-slate-200/80 lg:ps-10">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.3, ease }}
            className="text-xl sm:text-[1.4rem] font-semibold text-slate-800"
          >
            {c.question}
          </motion.p>
          <p className="mt-1 text-sm text-slate-400">{c.selectAllNote}</p>

          <div className="mt-5 space-y-3">
            {ROUTES.map((route, i) => {
              const isSelected = selections.has(i)

              let borderColor = isSelected
                ? 'border-violet-400 bg-violet-50'
                : 'border-slate-200 hover:border-slate-300 bg-white'

              if (submitted) {
                if (route.safe && isSelected) {
                  borderColor = 'border-emerald-400 bg-emerald-50'
                } else if (route.safe && !isSelected) {
                  borderColor = 'border-emerald-200 bg-emerald-50/50'
                } else if (!route.safe && isSelected) {
                  borderColor = 'border-rose-400 bg-rose-50'
                } else {
                  borderColor = 'border-slate-200 bg-white'
                }
              }

              return (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.45 + i * 0.08, ease }}
                  disabled={submitted}
                  onClick={() => toggle(i)}
                  className={`w-full rounded-xl border-2 ${borderColor} px-4 py-3 text-start transition-colors cursor-pointer disabled:cursor-default`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                      submitted
                        ? route.safe
                          ? 'border-emerald-400 bg-emerald-400'
                          : isSelected
                            ? 'border-rose-400 bg-rose-400'
                            : 'border-slate-300'
                        : isSelected
                          ? 'border-violet-500 bg-violet-500'
                          : 'border-slate-300'
                    }`}>
                      {(isSelected || (submitted && route.safe)) && (
                        <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M2.5 6.5L5 9l4.5-6" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-[15px] sm:text-base leading-snug text-slate-700">
                        {route.label}
                      </p>
                      {submitted && (isSelected || route.safe) && (
                        <motion.p
                          initial={{ opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, ease }}
                          className={`mt-1.5 text-sm leading-snug ${route.safe ? 'text-emerald-600' : 'text-rose-500'}`}
                        >
                          {route.feedback}
                        </motion.p>
                      )}
                    </div>
                  </div>
                </motion.button>
              )
            })}
          </div>

          {!submitted && selections.size > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease }}
              className="mt-5"
            >
              <button
                onClick={handleSubmit}
                className="rounded-xl bg-violet-600 px-6 py-2.5 text-[15px] font-semibold text-white hover:bg-violet-700 transition-colors cursor-pointer"
              >
                {c.submitLabel}
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

function StatusBarIcons() {
  return (
    <div className="flex items-center gap-1">
      <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
        <rect x="1" y="10" width="3" height="5" rx="0.5" />
        <rect x="5" y="7" width="3" height="8" rx="0.5" />
        <rect x="9" y="4" width="3" height="11" rx="0.5" />
        <rect x="13" y="1" width="3" height="14" rx="0.5" opacity="0.3" />
      </svg>
      <svg className="h-3.5 w-3.5 text-slate-900" viewBox="0 0 16 16" fill="currentColor">
        <path d="M8 3C5.5 3 3.3 4 1.8 5.6a.75.75 0 001.1 1C4.2 5.3 6 4.5 8 4.5s3.8.8 5.1 2.1a.75.75 0 001.1-1C12.7 4 10.5 3 8 3z" opacity="0.3" />
        <path d="M8 6.5c-1.7 0-3.2.7-4.3 1.8a.75.75 0 001.1 1C5.7 8.4 6.8 8 8 8s2.3.4 3.2 1.3a.75.75 0 001.1-1C11.2 7.2 9.7 6.5 8 6.5z" opacity="0.6" />
        <path d="M8 10c-.9 0-1.8.4-2.4 1a.75.75 0 001.1 1c.3-.4.8-.5 1.3-.5s1 .2 1.3.5a.75.75 0 001.1-1C9.8 10.4 8.9 10 8 10z" />
      </svg>
      <svg className="h-4.5 w-[22px] text-slate-900" viewBox="0 0 25 12" fill="currentColor">
        <rect x="0" y="1" width="21" height="10" rx="2" stroke="currentColor" strokeWidth="1" fill="none" />
        <rect x="22" y="4" width="2" height="4" rx="0.5" opacity="0.4" />
        <rect x="1.5" y="2.5" width="14" height="7" rx="1" />
      </svg>
    </div>
  )
}
